import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReportDisplay from '../report/viewer/ReportDisplay';
import StageShell from './common/StageShell';
import { getProject, setStage, getPipelinePayload } from '../../services/sdlcProject.service';
import { getReportHistory, getReportById } from '../../services/history.service';
import { generateSdlcPipeline } from '../../services/sdlcPipeline.service';
import { showSuccess, showError } from '../../services/notification.service';

/**
 * SDLC – Requirements stage.
 *
 * Shows the requirements report selected by the project. If none has been
 * selected (e.g. the user jumped straight to this tab) we offer a picker
 * over recent reports so any existing report from the database can become
 * the source-of-truth for the rest of the SDLC project.
 */
function RequirementsStage() {
  const navigate = useNavigate();
  const [project, setProject] = useState(getProject());
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [queueingPipeline, setQueueingPipeline] = useState(false);
  const [error, setError] = useState(null);

  const requirements = project?.requirements;

  useEffect(() => {
    if (requirements?.snapshot) return;
    let alive = true;
    (async () => {
      setLoadingHistory(true);
      try {
        const res = await getReportHistory();
        const list = res?.reports || res?.data || [];
        if (alive) setHistory(list);
      } catch (e) {
        if (alive) setError('Unable to load report history.');
      } finally {
        if (alive) setLoadingHistory(false);
      }
    })();
    return () => { alive = false; };
  }, [requirements]);

  const handlePick = async (reportId) => {
    try {
      const res = await getReportById(reportId);
      const snapshot = res?.report || res?.data || res;
      const next = setStage('requirements', { reportId, snapshot });
      setProject(next);
    } catch (e) {
      setError('Failed to load report.');
    }
  };

  const handleClear = () => {
    const next = setStage('requirements', null);
    setProject(next);
  };

  const handleQueuePipeline = async () => {
    if (!requirements?.snapshot) return;
    setQueueingPipeline(true);
    try {
      const payload = getPipelinePayload();
      const response = await generateSdlcPipeline({
        ...payload,
        action: 'generate-sdlc-pipeline',
        prompt: requirements.snapshot.title || requirements.snapshot.idea_title || requirements.snapshot.summary || 'Generate SDLC pipeline',
        human_gate_mode: 'approval_required',
        auto_deploy: false,
      });
      if (!response?.job_id) {
        throw new Error(response?.error || 'Failed to queue SDLC pipeline');
      }
      const next = setStage('pipeline', {
        jobId: response.job_id,
        status: response.status || 'accepted',
      });
      setProject(next);
      showSuccess('SDLC pipeline job queued. Each generated stage still requires approval before continuation.');
    } catch (pipelineError) {
      showError(pipelineError.response?.data?.error || pipelineError.message || 'Failed to queue SDLC pipeline');
    } finally {
      setQueueingPipeline(false);
    }
  };

  const inputs = [
    { label: 'Idea', value: project?.idea?.title || '' },
    { label: 'Summary', value: project?.idea?.summary || '' },
  ];

  return (
    <StageShell
      stageId="requirements"
      description="Pick or view the requirements report that drives the rest of the SDLC project."
      inputs={inputs}
      canContinue={Boolean(requirements?.snapshot)}
      actions={
        requirements?.snapshot ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="stage-btn" onClick={handleClear}>Pick a different report</button>
            <button className="stage-btn primary" onClick={handleQueuePipeline} disabled={queueingPipeline}>
              {queueingPipeline ? 'Queueing...' : 'Queue full pipeline'}
            </button>
          </div>
        ) : null
      }
    >
      {project?.pipeline?.jobId && (
        <div style={{ padding: 12, background: '#eff6ff', borderRadius: 8, color: '#1e3a8a', fontSize: '0.9rem', marginBottom: 14 }}>
          Pipeline job: <strong>{project.pipeline.jobId}</strong> ({project.pipeline.status || 'queued'})
        </div>
      )}

      {error && <div className="stage-empty"><div className="icon">⚠️</div>{error}</div>}

      {requirements?.snapshot ? (
        <ReportDisplay
          reportData={requirements.snapshot}
          onNewReport={() => navigate('/tools/sdlc/idea')}
          needsApproval={false}
        />
      ) : (
        <div>
          <p style={{ marginTop: 0 }}>
            No requirements report is bound to this SDLC project yet. Either generate one
            from the <a href="#/tools/sdlc/idea" onClick={(e) => { e.preventDefault(); navigate('/tools/sdlc/idea'); }}>Idea</a> stage,
            or pick an existing report from your history below.
          </p>
          {loadingHistory && <div className="stage-empty">Loading report history…</div>}
          {!loadingHistory && history.length === 0 && (
            <div className="stage-empty">
              <div className="icon">📭</div>
              No reports found. Generate one from the Idea stage to get started.
            </div>
          )}
          <div className="stage-file-list">
            {history.map((r) => {
              const id = r._id || r.report_id;
              return (
                <button
                  key={id}
                  type="button"
                  className="stage-file-item"
                  style={{ textAlign: 'left', cursor: 'pointer', background: 'transparent' }}
                  onClick={() => handlePick(id)}
                >
                  <span>{r.title || r.idea_title || id}</span>
                  <span className="meta">{r.created_at?.slice(0, 10) || ''}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </StageShell>
  );
}

export default RequirementsStage;
